import { Link, useLocation, useNavigate } from "react-router-dom";
import { Activity, Table2, Star, FlaskConical, LogOut, Crown, Wallet } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AlertsBell from "./AlertsBell";

const NAV = [
  { to: "/", label: "Scanner", icon: Table2, testid: "nav-scanner" },
  { to: "/watchlist", label: "Watchlist", icon: Star, testid: "nav-watchlist" },
  { to: "/trades", label: "P&L", icon: Wallet, testid: "nav-trades" },
  { to: "/backtest", label: "Backtest", icon: FlaskConical, testid: "nav-backtest" },
];

export default function Layout({ children }) {
  const { user, access, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0A]">
        <div className="flex h-14 items-center gap-6 px-4 sm:px-6">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
            <Activity size={20} className="text-[#00F0FF]" strokeWidth={2.5} />
            <span className="font-head text-lg font-black tracking-tight">KLONDIKE<span className="text-[#00F0FF]">SWING</span></span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n) => {
              const active = loc.pathname === n.to;
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={`flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm transition-colors duration-200 ${
                    active ? "bg-white/10 text-white" : "text-gray-400 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon size={15} /> {n.label}
                </Link>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-4">
            <span className="hidden font-mono-sg text-xs text-gray-500 sm:inline">
              Setup Quality, not win probability
            </span>
            <PlanBadge access={access} onClick={() => nav("/paywall")} />
            <span className="hidden font-mono-sg text-xs text-gray-400 lg:inline" data-testid="user-email">
              {user?.email}
            </span>
            <AlertsBell />
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); nav("/login"); }}
              className="flex items-center gap-1.5 rounded-sm border border-white/10 px-2.5 py-1.5 text-xs text-gray-400 transition-colors duration-200 hover:border-white/20 hover:text-white"
            >
              <LogOut size={13} /> Exit
            </button>
          </div>
        </div>
        <nav className="flex items-center gap-1 border-t border-white/10 px-2 py-1 md:hidden">
          {NAV.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs ${active ? "bg-white/10 text-white" : "text-gray-400"}`}>
                <Icon size={14} /> {n.label}
              </Link>
            );
          })}
        </nav>
      </header>
      {access?.reason === "trial" && <TrialBanner days={access.trial_days_left} onUpgrade={() => nav("/paywall")} />}
      <main>{children}</main>
    </div>
  );
}

function TrialBanner({ days, onUpgrade }) {
  const urgent = days <= 3;
  const cls = urgent
    ? "border-[#FF7F00]/40 bg-[#FF7F00]/10 text-[#FF7F00]"
    : "border-[#FFD700]/30 bg-[#FFD700]/10 text-[#FFD700]";
  return (
    <div data-testid="trial-banner" className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b px-4 py-2 text-center font-mono-sg text-xs ${cls}`}>
      <span>
        {days === 1 ? "Last day of your free trial" : `${days} days left in your free trial`}
        {urgent ? " — subscribe now to keep your scanner, alerts & backtests." : " · KlondikeSwing Pro is $20/mo."}
      </span>
      <button data-testid="trial-upgrade-btn" onClick={onUpgrade}
        className="rounded-sm bg-white/10 px-2.5 py-0.5 font-bold text-white transition-colors duration-200 hover:bg-white/20">
        Upgrade →
      </button>
    </div>
  );
}

function PlanBadge({ access, onClick }) {
  if (!access) return null;
  if (access.reason === "subscription") {
    return (
      <button data-testid="plan-badge" onClick={onClick} className="inline-flex items-center gap-1 rounded-sm border border-[#00FF66]/40 bg-[#00FF66]/10 px-2 py-1 font-mono-sg text-[11px] font-bold text-[#00FF66] transition-colors duration-200 hover:bg-[#00FF66]/20">
        <Crown size={12} /> PRO
      </button>
    );
  }
  if (access.reason === "admin") {
    return (
      <span data-testid="plan-badge" className="inline-flex items-center gap-1 rounded-sm border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 py-1 font-mono-sg text-[11px] font-bold text-[#00F0FF]">
        <Crown size={12} /> ADMIN
      </span>
    );
  }
  if (access.reason === "trial") {
    return (
      <button data-testid="plan-badge" onClick={onClick}
        className="inline-flex items-center gap-1 rounded-sm border border-[#FFD700]/40 bg-[#FFD700]/10 px-2 py-1 font-mono-sg text-[11px] font-bold text-[#FFD700] transition-colors duration-200 hover:bg-[#FFD700]/20">
        Trial · {access.trial_days_left}d — Upgrade
      </button>
    );
  }
  return null;
}
